import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { onMounted, reactive, ref } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import Card from '@/views/dashboard/_components/card/card.vue'
import PageSubscribeBar from '@/views/dashboard/_components/subscribe-bar/page-subscribe-bar.vue'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { SecurityEventService } from './security-event.service'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { getTimeRangeByEnum } from '@/utils/time'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import {
  DistributionDimension,
  SecurityDisposeStatus,
  SecurityEventLevel,
  SecurityEventType,
} from './constants/enums'
import type { SecurityEventListItem, SecurityEventOverview } from './types'
import SecurityEventStatisticsCards from './_components/security-event-statistics-cards.vue'
import SecurityEventDistributionChart from './_components/security-event-distribution-chart.vue'
import SecurityEventList from './_components/security-event-list.vue'
import SecurityEventDetail from './_components/security-event-detail.vue'
import SecurityEventDispose from './_components/security-event-dispose.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: { flag: { type: Boolean, default: true } },
  setup(__props) {



const injector = useReflectiveInjector([SecurityEventService])
const securityEventService = injector.get(SecurityEventService)
const servicePermissionService = injector.get(ServicePermissionService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.security_event)

const overview = reactive<SecurityEventOverview>({
  new_events_30d: 0,
  pending_count: 0,
  trusted_count: 0,
  ignored_count: 0,
})
const drawerVisible = ref(false)
const drawerListKey = ref(0)
const drawerInitialStatus = ref<SecurityDisposeStatus | undefined>()
const drawerInitialLevel = ref<SecurityEventLevel | undefined>()
const drawerInitialEventType = ref<SecurityEventType | undefined>()
const drawerInitialAttackTactic = ref<string | undefined>()
const drawerInitialTimeRange = ref<[number, number] | undefined>()

const detailVisible = ref(false)
const disposeVisible = ref(false)
const disposeBusinessId = ref<string>()
const disposeListRow = ref<SecurityEventListItem | null>(null)
const currentRow = ref<SecurityEventListItem | null>(null)
const previewListRef = ref()
const drawerListRef = ref()

async function loadOverview () {
  if (!hasPermission) return
  try {
    const data = await securityEventService.getOverview()
    Object.assign(overview, data)
  } catch (e) {
    console.error(e)
  }
}

function openDrawer (opts: {
  status?: SecurityDisposeStatus
  level?: SecurityEventLevel
  eventType?: SecurityEventType
  attackTactic?: string
  timeRange?: [number, number]
}) {
  drawerInitialStatus.value = opts.status
  drawerInitialLevel.value = opts.level
  drawerInitialEventType.value = opts.eventType != null ? String(opts.eventType) : undefined
  drawerInitialAttackTactic.value = opts.attackTactic
  drawerInitialTimeRange.value = opts.timeRange
  drawerListKey.value += 1
  drawerVisible.value = true
}

function onStatClick (key: 'new30d' | 'pending' | 'trusted' | 'ignored') {
  if (!hasPermission) return
  const [start, end] = getTimeRangeByEnum(TimeRange.DAYS_30)
  const timeRange = start != null && end != null ? ([start, end] as [number, number]) : undefined
  if (key === 'new30d') {
    openDrawer({ status: undefined, timeRange })
    return
  }
  const statusMap = {
    pending: SecurityDisposeStatus.Pending,
    trusted: SecurityDisposeStatus.Trusted,
    ignored: SecurityDisposeStatus.Ignored,
  }
  openDrawer({ status: statusMap[key], timeRange })
}

function onPieSliceClick (dimension: string, value: string | number) {
  if (!hasPermission) return
  const [start, end] = getTimeRangeByEnum(TimeRange.DAYS_30)
  const timeRange = start != null && end != null ? ([start, end] as [number, number]) : undefined
  if (dimension === DistributionDimension.Level) {
    openDrawer({ level: value as SecurityEventLevel, timeRange })
  } else if (dimension === DistributionDimension.Category) {
    openDrawer({ eventType: value as SecurityEventType, timeRange })
  } else {
    openDrawer({ attackTactic: String(value), timeRange })
  }
}

function viewAll () {
  if (!hasPermission) return
  openDrawer({
    status: SecurityDisposeStatus.Pending,
    level: undefined,
    eventType: undefined,
    attackTactic: undefined,
    timeRange: undefined,
  })
}

function openDetail (row: SecurityEventListItem) {
  currentRow.value = row
  detailVisible.value = true
}

function openDispose (row: SecurityEventListItem) {
  disposeListRow.value = row
  disposeBusinessId.value = row.business_id
  disposeVisible.value = true
}

function openDisposeFromDetail (detail: SecurityEventListItem) {
  disposeListRow.value = detail
  disposeBusinessId.value = detail.business_id
  detailVisible.value = false
  disposeVisible.value = true
}

function onDisposeViewDetail () {
  const row = disposeListRow.value
  if (!row?.business_id) return
  currentRow.value = row
  detailVisible.value = true
}

function onDisposeSuccess () {
  refreshAllLists()
}

function onDetailRefresh () {
  refreshAllLists()
}

function refreshAllLists () {
  void loadOverview()
  previewListRef.value?.refreshList?.()
  drawerListRef.value?.refreshList?.()
}

onMounted(() => void loadOverview())

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(SecurityEventStatisticsCards, {
      overview: overview,
      onStatClick: onStatClick
    }, null, 8, ["overview"]),
    _createVNode(PageSubscribeBar, {
      "normal-permission-key": _unref(PermissionFe).security_event
    }, null, 8, ["normal-permission-key"]),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "cm-ui-mt16"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 8 }, {
          default: _withCtx(() => [
            _createVNode(SecurityEventDistributionChart, {
              style: {"height":"380px"},
              dimension: _unref(DistributionDimension).Level,
              title: _ctx.$t('securityEvent.chartLevel'),
              "has-permission": _unref(hasPermission),
              onSliceClick: onPieSliceClick
            }, null, 8, ["dimension", "title", "has-permission"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 8 }, {
          default: _withCtx(() => [
            _createVNode(SecurityEventDistributionChart, {
              style: {"height":"380px"},
              dimension: _unref(DistributionDimension).Category,
              title: _ctx.$t('securityEvent.chartCategory'),
              "has-permission": _unref(hasPermission),
              onSliceClick: onPieSliceClick
            }, null, 8, ["dimension", "title", "has-permission"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 8 }, {
          default: _withCtx(() => [
            _createVNode(SecurityEventDistributionChart, {
              style: {"height":"380px"},
              dimension: _unref(DistributionDimension).AttackStage,
              title: _ctx.$t('securityEvent.chartAttack'),
              "has-permission": _unref(hasPermission),
              onSliceClick: onPieSliceClick
            }, null, 8, ["dimension", "title", "has-permission"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      title: _ctx.$t('securityEvent.eventListTitle'),
      size: "second",
      class: "cm-ui-mt16",
      cardHeaderStyle: {marginBottom:'0'}
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, {
          class: "security-event-tag",
          size: "small"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(SecurityEventList, {
          onViewAll: viewAll,
          ref_key: "previewListRef",
          ref: previewListRef,
          "has-permission": _unref(hasPermission),
          onDetail: openDetail,
          onDispose: openDispose
        }, null, 8, ["has-permission"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_component_el_drawer, {
      modelValue: drawerVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((drawerVisible).value = $event)),
      size: "80%",
      title: _ctx.$t('securityEvent.allEvents'),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(SecurityEventList, {
          ref_key: "drawerListRef",
          ref: drawerListRef,
          key: drawerListKey.value,
          "view-mode": _unref(TableViewMode).full,
          "has-permission": _unref(hasPermission),
          "initial-handle-status": drawerInitialStatus.value,
          "initial-event-level": drawerInitialLevel.value,
          "initial-event-type": drawerInitialEventType.value,
          "initial-attack-tactic": drawerInitialAttackTactic.value,
          "initial-time-range-unix": drawerInitialTimeRange.value,
          onDetail: openDetail,
          onDispose: openDispose,
          style: {"padding":"10px 16px"}
        }, null, 8, ["view-mode", "has-permission", "initial-handle-status", "initial-event-level", "initial-event-type", "initial-attack-tactic", "initial-time-range-unix"]))
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(SecurityEventDetail, {
      modelValue: detailVisible.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((detailVisible).value = $event)),
      "list-row": currentRow.value,
      onDispose: openDisposeFromDetail,
      onRefresh: onDetailRefresh
    }, null, 8, ["modelValue", "list-row"]),
    _createVNode(SecurityEventDispose, {
      modelValue: disposeVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((disposeVisible).value = $event)),
      "business-id": disposeBusinessId.value,
      "list-row": disposeListRow.value,
      onSuccess: onDisposeSuccess,
      onViewDetail: onDisposeViewDetail
    }, null, 8, ["modelValue", "business-id", "list-row"])
  ], 64))
}
}

})